import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, CalendarPlus, FileText, Plus, Receipt, UserPlus, X
} from 'lucide-react'
import {
  useCreateInvoice, useCrewAssignment, useJob, useMoveStage, useStages, useUsers
} from '@/lib/queries.js'
import { money, formatDateTime, humanise, timeAgo } from '@/lib/format.js'
import { fullName } from '@/lib/utils.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle, DetailRow, PageHeader } from '@/components/ui/card.jsx'
import { Alert, EmptyState, Spinner } from '@/components/ui/feedback.jsx'
import { StatusBadge } from '@/components/ui/badge.jsx'
import { Select } from '@/components/ui/select.jsx'
import { Label } from '@/components/ui/field.jsx'
import { NewAppointmentDialog } from './NewAppointmentDialog.jsx'
import { humanMessage, isWorkflow } from '@/lib/errors.js'

/**
 * Job detail (Phase 1b screen 5) — the working screen of the whole product.
 *
 * Everything about one job in one place: where it is in the pipeline, what has
 * been quoted, what is scheduled, who is on it, and what has happened to it.
 *
 * ── STAGE MOVES HAVE EXACTLY ONE PATH ───────────────────────────────────────
 * The picker calls POST /jobs/:id/stage, never PATCH. The API refuses a stage
 * change on the generic PATCH with 400 `use_stage_endpoint` precisely so that
 * history can never be skipped — this screen is where that guarantee becomes
 * visible, because the timeline below updates from the same mutation.
 *
 * Mobile-first (D8): this is a screen someone opens standing in a client's
 * kitchen, so it is a single column on a phone and two on desktop.
 */
export function JobDetail () {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isPending, error } = useJob(id)
  const stagesQuery = useStages()
  const usersQuery = useUsers()
  const moveStage = useMoveStage()
  const createInvoice = useCreateInvoice()
  const [actionError, setActionError] = React.useState(null)
  const [scheduling, setScheduling] = React.useState(false)

  if (isPending) {
    return <div className="flex justify-center py-16"><Spinner label="Loading job" /></div>
  }

  if (error) {
    // A failed detail screen still gets a real <h1>. Rendering only an alert
    // leaves the page with no heading at all — a screen reader announces
    // nothing to orient by, and visually it reads as a fragment of a page
    // rather than a page that has something to say.
    return (
      <>
        <PageHeader back={<BackLink />} title="Could not load this job" />
        <Alert tone="danger">{humanMessage(error)}</Alert>
      </>
    )
  }

  const { job, estimates, appointments, assignments, stage_history: history } = data
  const stages = stagesQuery.data ?? []
  const users = usersQuery.data ?? []
  const currentStage = stages.find(s => s.id === job.pipeline_stage_id)

  const acceptedEstimate = estimates.find(e => e.status === 'accepted')

  async function handleStageChange (stageId) {
    setActionError(null)
    try {
      await moveStage.mutateAsync({ jobId: job.id, stageId })
    } catch (err) {
      setActionError(err)
    }
  }

  async function handleCreateInvoice () {
    setActionError(null)
    try {
      const { invoice } = await createInvoice.mutateAsync(
        acceptedEstimate ? { estimate_id: acceptedEstimate.id } : { job_id: job.id }
      )
      navigate(`/invoices/${invoice.id}`)
    } catch (err) {
      setActionError(err)
    }
  }

  return (
    <>
      <PageHeader
        back={<BackLink />}
        title={job.title}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="tabular font-mono text-xs">{job.job_number}</span>
            <span aria-hidden="true">·</span>
            {job.clients
              ? <Link to={`/clients/${job.client_id}`} className="text-brand hover:underline">
                  {fullName(job.clients)}
                </Link>
              : <span>Unknown client</span>}
            <span aria-hidden="true">·</span>
            <StatusBadge status={job.status} />
          </span>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setScheduling(true)}>
              <CalendarPlus /> Schedule
            </Button>
            <Button asChild>
              <Link to={`/jobs/${job.id}/estimates/new`}><Plus /> Estimate</Link>
            </Button>
          </>
        }
      />

      {actionError && (
        <Alert
          // A workflow state is not a failure — the user did something
          // reasonable the current state does not allow.
          tone={isWorkflow(actionError) ? 'warn' : 'danger'}
          className="mb-4"
        >
          {humanMessage(actionError)}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* ---- stage ------------------------------------------------- */}
          <Card>
            <CardHeader><CardTitle>Pipeline stage</CardTitle></CardHeader>
            <CardBody className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="stage-picker" className="sr-only">Pipeline stage</Label>
                <Select
                  id="stage-picker"
                  data-testid="stage-picker"
                  value={job.pipeline_stage_id ?? ''}
                  onChange={e => handleStageChange(e.target.value)}
                  disabled={moveStage.isPending}
                >
                  {!job.pipeline_stage_id && <option value="">Not on the board</option>}
                  {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </div>
              {currentStage?.is_won_stage && (
                <p className="text-sm text-success-ink">This stage counts as won.</p>
              )}
              {currentStage?.is_lost_stage && (
                <p className="text-sm text-ink-muted">This stage counts as lost.</p>
              )}
              {moveStage.isPending && <Spinner label="Moving" />}
            </CardBody>
          </Card>

          {/* ---- estimates --------------------------------------------- */}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Estimates</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to={`/jobs/${job.id}/estimates/new`}>New</Link>
              </Button>
            </CardHeader>
            <CardBody className="p-0">
              {estimates.length === 0
                ? <p className="px-4 py-6 text-center text-sm text-ink-muted sm:px-5">
                    No estimates yet. Quote this job to move it forward.
                  </p>
                : (
                  <ul className="divide-y divide-line">
                    {estimates.map(est => (
                      <li key={est.id} className="flex items-center">
                        <Link
                          to={`/estimates/${est.id}`}
                          className="flex min-h-tap flex-1 items-center gap-3 px-4 py-3 hover:bg-surface-sunken sm:px-5"
                          data-testid="estimate-row"
                        >
                          <FileText className="size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                          <span className="tabular min-w-0 flex-1 truncate font-mono text-xs text-ink-muted">
                            {est.estimate_number}
                          </span>
                          <StatusBadge status={est.status} />
                          <span className="tabular text-sm font-medium text-ink">{money(est.total)}</span>
                        </Link>

                        {/* D26.2 + D27.7. Extra work on a job that is already
                            signed for is a SUPPLEMENTAL, never an edit — a
                            signed estimate is immutable (D17) and the API
                            refuses it with its own slug. Offering the correct
                            action right here is what keeps a contractor from
                            finding that out mid-conversation.

                            D27.7 is a UX constraint rather than a preference:
                            this is two taps from the job on a phone, and the
                            editor it opens has the in-person signing button on
                            the same screen. */}
                        {est.status === 'accepted' && (
                          <Button
                            asChild variant="ghost" size="sm" className="mr-2 shrink-0"
                            data-testid="add-supplemental"
                          >
                            <Link to={`/jobs/${job.id}/estimates/new?supplemental=${est.id}`}>
                              <Plus /> Add work
                            </Link>
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                  )}
            </CardBody>
          </Card>

          {/* ---- invoices ---------------------------------------------- */}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Invoice</CardTitle>
            </CardHeader>
            <CardBody>
              {/* Invoicing is deliberately gated on an accepted estimate in the
                  UI as well as the API (409 estimate_not_accepted). Offering a
                  button that always 409s would be a worse experience than
                  explaining the prerequisite. */}
              {acceptedEstimate
                ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-ink-muted">
                      {money(acceptedEstimate.total)} accepted — ready to invoice.
                    </p>
                    <Button
                      onClick={handleCreateInvoice}
                      loading={createInvoice.isPending}
                      data-testid="create-invoice"
                    >
                      <Receipt /> Create invoice
                    </Button>
                  </div>
                  )
                : (
                  <p className="text-sm text-ink-muted">
                    Once an estimate is accepted, you can raise the invoice from it here.
                  </p>
                  )}
            </CardBody>
          </Card>

          {/* ---- appointments ------------------------------------------ */}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Scheduled</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setScheduling(true)}>Add</Button>
            </CardHeader>
            <CardBody className="p-0">
              {appointments.length === 0
                ? <p className="px-4 py-6 text-center text-sm text-ink-muted sm:px-5">Nothing scheduled.</p>
                : (
                  <ul className="divide-y divide-line">
                    {appointments.map(appt => (
                      <li key={appt.id} className="px-4 py-3 sm:px-5" data-testid="job-appointment">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="flex-1 text-sm font-medium text-ink">{appt.title}</span>
                          <StatusBadge status={appt.status} />
                        </div>
                        <p className="mt-0.5 text-sm text-ink-muted">
                          {formatDateTime(appt.scheduled_start)} · {humanise(appt.type)}
                        </p>
                      </li>
                    ))}
                  </ul>
                  )}
            </CardBody>
          </Card>
        </div>

        {/* ---- sidebar ------------------------------------------------- */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardBody>
              <dl className="space-y-2.5">
                <DetailRow label="Type">{humanise(job.job_type)}</DetailRow>
                <DetailRow label="Priority">{humanise(job.priority)}</DetailRow>
                <DetailRow label="Estimated value">
                  {Number(job.estimated_value) > 0 ? money(job.estimated_value) : null}
                </DetailRow>
                <DetailRow label="Created">{formatDateTime(job.created_at)}</DetailRow>
                {job.description && <DetailRow label="Notes">{job.description}</DetailRow>}
              </dl>
            </CardBody>
          </Card>

          <CrewCard job={job} assignments={assignments} users={users} />

          {/* ---- stage history ------------------------------------------ */}
          <Card>
            <CardHeader><CardTitle>History</CardTitle></CardHeader>
            <CardBody>
              {history.length === 0
                ? <p className="text-sm text-ink-muted">No movement recorded yet.</p>
                : (
                  <ol className="space-y-3" data-testid="stage-history">
                    {history.map(entry => (
                      <li key={entry.id} className="flex gap-3">
                        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-line-strong" aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-ink">
                            {entry.from_stage
                              ? <>Moved from <strong className="font-medium">{entry.from_stage.name}</strong> to{' '}
                                <strong className="font-medium">{entry.to_stage?.name}</strong></>
                              : <>Created in <strong className="font-medium">{entry.to_stage?.name}</strong></>}
                          </p>
                          {entry.reason && <p className="text-sm text-ink-muted">{entry.reason}</p>}
                          <p className="text-xs text-ink-subtle">{timeAgo(entry.created_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                  )}
            </CardBody>
          </Card>
        </div>
      </div>

      <NewAppointmentDialog open={scheduling} onOpenChange={setScheduling} jobId={job.id} users={users} />
    </>
  )
}

/**
 * Crew assignment.
 *
 * D9 is visible here: "assign a crew" is in scope while "have a crew" is not,
 * so a brand-new tenant sees exactly themselves in this list. The empty-ish
 * case has to read as normal rather than broken.
 */
function CrewCard ({ job, assignments, users }) {
  const assign = useCrewAssignment()
  const [error, setError] = React.useState(null)
  const assignedIds = new Set(assignments.map(a => a.user_id))
  const available = users.filter(u => !assignedIds.has(u.id))
  const byId = new Map(users.map(u => [u.id, u]))

  async function run (payload) {
    setError(null)
    try { await assign.mutateAsync(payload) } catch (err) { setError(err) }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Crew</CardTitle></CardHeader>
      <CardBody className="space-y-3">
        {error && <Alert tone="danger">{humanMessage(error)}</Alert>}

        {assignments.length === 0
          ? <p className="text-sm text-ink-muted">Nobody assigned yet.</p>
          : (
            <ul className="space-y-2">
              {assignments.map(a => {
                const user = byId.get(a.user_id)
                return (
                  <li key={a.id} className="flex items-center gap-2" data-testid="crew-member">
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">
                      {user ? fullName(user) : 'Unknown user'}
                      <span className="ml-1.5 text-ink-subtle">{humanise(a.role_on_job)}</span>
                    </span>
                    <Button
                      variant="ghost" size="icon-sm"
                      aria-label={`Remove ${user ? fullName(user) : 'crew member'}`}
                      onClick={() => run({ jobId: job.id, userId: a.user_id, remove: true })}
                    >
                      <X />
                    </Button>
                  </li>
                )
              })}
            </ul>
            )}

        {available.length > 0 && (
          <div className="space-y-1.5">
            <Label htmlFor="crew-picker" className="sr-only">Assign someone</Label>
            <Select
              id="crew-picker"
              data-testid="crew-picker"
              value=""
              onChange={e => e.target.value && run({ jobId: job.id, userId: e.target.value, roleOnJob: 'lead' })}
              disabled={assign.isPending}
            >
              <option value="">Assign someone…</option>
              {available.map(u => <option key={u.id} value={u.id}>{fullName(u)}</option>)}
            </Select>
          </div>
        )}

        {available.length === 0 && assignments.length > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-ink-subtle">
            <UserPlus className="size-3.5" aria-hidden="true" />
            Everyone in this workspace is on this job.
          </p>
        )}
      </CardBody>
    </Card>
  )
}

function BackLink () {
  return (
    <Link
      to="/"
      className="mb-2 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
    >
      <ArrowLeft className="size-4" aria-hidden="true" /> Pipeline
    </Link>
  )
}
